import type {
  ApplicablePassenger,
  ExtrasResponse,
  Passenger,
} from '@models/api/extras-response.model';
import type { FlightTabItem } from '@models/landing/api/baggage.model';
import type { CodeTypeItem, CodeTypeList, MealsList } from '@models/master-data/master-data.model';
import { sortPassengersWithAssignedInfants } from '@shared/components/ssr-passenger.util';

const MEALS_SSR_TYPE = 'MEAL';

export type MealsTabUnavailableReason = 'interline' | 'cut-off' | 'no-meals';

const resolveMealCodeTypeItem = (
  codeType: string,
  codeTypeList: CodeTypeList,
  mealsList?: MealsList,
): CodeTypeItem | undefined => {
  const directMatch = codeTypeList.find((item) => item.codeID === codeType);
  if (directMatch) {
    return directMatch;
  }

  const matchedMeal = mealsList?.find((meal) => meal.code === codeType);
  if (!matchedMeal?.masterCode) {
    return undefined;
  }

  return codeTypeList.find((item) => item.codeID === matchedMeal.masterCode);
};

const resolveMealCodeName = (
  codeType: string,
  codeTypeList: CodeTypeList,
  mealsList?: MealsList,
): string | null => {
  // Prioritize mealsList from session storage (language-specific)
  const mealNameFromMealsList = mealsList?.find((meal) => meal.code === codeType)?.name;
  if (mealNameFromMealsList) {
    return mealNameFromMealsList;
  }

  // Fall back to codeTypeList if meal is not found in mealsList
  const matchedCodeType = resolveMealCodeTypeItem(codeType, codeTypeList, mealsList);

  const codeTypeName = matchedCodeType?.shortCodeName ?? matchedCodeType?.codeName;
  if (codeTypeName) {
    return codeTypeName;
  }

  return null;
};

/**
 * API payload structure for adding/removing meals
 */
export interface MealsAddApiPayload {
  [key: string]: unknown;
  ssrType?: string;
  logicalFlightId: string;
  physicalFlightId?: string;
  amount?: string;
  paxJourneyId?: string;
  resPaxId: number;
  recordNumber?: number;
  quantityAvailable?: number;
  chargeComment?: string;
  codeType: string;
  isBusiness?: boolean;
  secureHash: string;
}

/**
 * Meals quote option for display
 */
export interface MealsQuoteOption {
  codeType: string;
  codeName: string;
  receiptDisplay: string;
  amountRaw?: string;
  amount: number;
  currency: string;
  applicablePax?: ApplicablePassenger[];
  quantityAvailable?: number;
  isBusiness?: boolean;
  secureHash: string;
  chargeComment: string;
  isMostPopular?: boolean;
  mostPopularLabel?: string;
  isDefaultSelected?: boolean;
  isIncluded?: boolean;
}

/**
 * Meals data for a flight mapped to UI view
 */
export interface MealsFlightTabData {
  ssrType?: string;
  logicalFlightId: string;
  physicalFlightId?: string;
  flightNumber: string;
  mealOptions: MealsQuoteOption[];
  totalAmount: number;
  currency: string;
  isInterline: boolean;
  isWithinCutOff: boolean;
  isUnavailable: boolean;
  unavailableReason?: MealsTabUnavailableReason;
}

/**
 * Mapped meals data for modal display
 */
export interface MealsModalViewData {
  flightTabs: MealsFlightTabData[];
  passengerList: Passenger[];
}

/**
 * Map meal info to meal options
 */
const mapMealInfoToOptions = (
  mealsInfo: NonNullable<ExtrasResponse['mealQuotes'][number]['mealsInfo']>,
  currency: string,
  codeTypeList: CodeTypeList,
  mealsList?: MealsList,
  preferredServices?: readonly string[],
): MealsQuoteOption[] => {
  const mappedMeals = mealsInfo.map((meal) => {
    const codeName =
      resolveMealCodeName(meal.codeType, codeTypeList, mealsList) ??
      meal.chargeComment ??
      meal.codeType;

    // Check if this meal codeType is in the preferredServices list
    const isMostPopular = preferredServices?.some((serviceCode) => serviceCode === meal.codeType);

    return {
      codeType: meal.codeType,
      codeName,
      receiptDisplay: codeName,
      amountRaw: meal.amount,
      amount: Number(meal.amount),
      currency,
      applicablePax: meal.applicablePax,
      quantityAvailable: meal.quantityAvailable,
      isBusiness: meal.isBusiness,
      secureHash: meal.secureHash,
      chargeComment: meal.chargeComment,
      isMostPopular,
      isDefaultSelected: meal.isDefaultSelected,
    };
  });

  mappedMeals.sort(sortMealsOptions);

  return mappedMeals;
};

const sortMealsOptions = (a: MealsQuoteOption, b: MealsQuoteOption): number => {
  // 1. Included meals come first
  if (a.isIncluded && !b.isIncluded) {
    return -1;
  }
  if (!a.isIncluded && b.isIncluded) {
    return 1;
  }

  // 2. Keep default selected option pinned first within each group
  if (a.isDefaultSelected) {
    return -1;
  }

  if (b.isDefaultSelected) {
    return 1;
  }

  // 3. Sort alphabetically by name
  return a.codeName.localeCompare(b.codeName);
};

const mergeMealOptions = (
  quoteMealsOptions: MealsQuoteOption[],
  includedMealsOptions: MealsQuoteOption[],
): MealsQuoteOption[] => {
  const quoteCodeTypes = new Set(quoteMealsOptions.map((meal) => meal.codeType));
  const missingIncludedMeals = includedMealsOptions.filter(
    (meal) => !quoteCodeTypes.has(meal.codeType),
  );

  // Included meals should appear first in the dropdown
  return [...missingIncludedMeals, ...quoteMealsOptions].sort(sortMealsOptions);
};

/**
 * Extract included meals from passenger list for a specific quote
 */
const extractIncludedMealsForQuote = (
  passengerList: Passenger[],
  quote: ExtrasResponse['mealQuotes'][number],
  codeTypeList: CodeTypeList,
  mealsList?: MealsList,
): MealsQuoteOption[] => {
  const includedMeals = new Map<string, { codeType: string; isPurchased: boolean }>();

  passengerList.forEach((passenger) => {
    const flightDetail = passenger.flightsDetail?.find(
      (fd) =>
        fd.logicalFlightId === quote.logicalFlightId &&
        (quote.physicalFlightId ? fd.physicalFlightId === quote.physicalFlightId : true),
    );

    flightDetail?.includedExtrasList?.forEach((extra) => {
      if (String(extra.ssrType).toUpperCase() === MEALS_SSR_TYPE) {
        includedMeals.set(extra.codeType, {
          codeType: extra.codeType,
          isPurchased: extra.isPurchased,
        });
      }
    });
  });

  return Array.from(includedMeals.values()).map((meal) => {
    const codeName = resolveMealCodeName(meal.codeType, codeTypeList, mealsList) ?? meal.codeType;

    return {
      codeType: meal.codeType,
      codeName,
      receiptDisplay: codeName,
      amountRaw: '0',
      amount: 0,
      currency: quote.currency,
      applicablePax: [],
      quantityAvailable: 0,
      isBusiness: false,
      secureHash: '',
      chargeComment: '',
      isIncluded: !meal.isPurchased, // Free/included only if isPurchased is false
    };
  });
};

const hasIncludedMealsForQuote = (
  passengerList: Passenger[],
  quote: ExtrasResponse['mealQuotes'][number],
): boolean => {
  return passengerList.some((passenger) => {
    const flightDetail = passenger.flightsDetail?.find(
      (fd) =>
        fd.logicalFlightId === quote.logicalFlightId &&
        (quote.physicalFlightId ? fd.physicalFlightId === quote.physicalFlightId : true),
    );

    return Boolean(
      flightDetail?.includedExtrasList?.some(
        (extra) => String(extra.ssrType).toUpperCase() === MEALS_SSR_TYPE,
      ),
    );
  });
};

const resolveUnavailableReason = (
  quote: ExtrasResponse['mealQuotes'][number],
  passengerList: Passenger[],
  flightTabItem?: FlightTabItem,
): MealsTabUnavailableReason | undefined => {
  if (flightTabItem?.isInterline) {
    return 'interline';
  }

  if (quote.isWithinCutOff === true) {
    return 'cut-off';
  }

  if (quote.mealsInfo === null && !hasIncludedMealsForQuote(passengerList, quote)) {
    return 'no-meals';
  }

  return undefined;
};

/**
 * Map meals quotes to modal view data
 */
export const mapMealsQuotesToModalView = (
  mealQuotes: ExtrasResponse['mealQuotes'],
  passengerList: Passenger[],
  flightTabItems?: FlightTabItem[],
  codeTypeList?: CodeTypeList,
  mealsList?: MealsList,
  preferredServices?: readonly string[],
): MealsModalViewData => {
  const flightTabs: MealsFlightTabData[] = mealQuotes.map((quote, index) => {
    const currentFlightTabItem = flightTabItems?.[index];
    const unavailableReason = resolveUnavailableReason(quote, passengerList, currentFlightTabItem);

    // Extract included meals from passenger list
    const includedMealsOptions = extractIncludedMealsForQuote(
      passengerList,
      quote,
      codeTypeList ?? [],
      mealsList,
    );

    // Get meals from quotes if available
    const quoteMealsOptions =
      quote.mealsInfo !== null
        ? mapMealInfoToOptions(
            quote.mealsInfo,
            quote.currency,
            codeTypeList ?? [],
            mealsList,
            preferredServices,
          )
        : [];

    const mealOptions = mergeMealOptions(quoteMealsOptions, includedMealsOptions);

    return {
      ssrType: quote.ssrType,
      logicalFlightId: quote.logicalFlightId,
      physicalFlightId: quote.physicalFlightId,
      flightNumber: quote.flightNumber,
      mealOptions,
      totalAmount: Number(quote.totalAmount),
      currency: quote.currency,
      isInterline: Boolean(currentFlightTabItem?.isInterline),
      isWithinCutOff: quote.isWithinCutOff === true,
      isUnavailable: unavailableReason !== undefined,
      unavailableReason,
    };
  });

  return {
    flightTabs,
    passengerList: sortPassengersWithAssignedInfants(passengerList),
  };
};

/**
 * Build meals payload for single passenger and flight
 */
export const buildMealsPayload = (
  logicalFlightId: string,
  resPaxId: number,
  selectedMeal: MealsQuoteOption | null,
): MealsAddApiPayload => {
  return {
    logicalFlightId,
    resPaxId,
    codeType: selectedMeal?.codeType ?? '',
    secureHash: selectedMeal?.secureHash ?? '',
  };
};

/**
 * Build group of meals payloads for all passengers on a flight
 */
export const buildMealsPayloadsForFlight = (
  logicalFlightId: string,
  selectedMealByResPaxId: Record<number, MealsQuoteOption | null>,
): MealsAddApiPayload[] => {
  return Object.entries(selectedMealByResPaxId).map(([resPaxIdStr, selectedMeal]) => {
    const resPaxId = Number(resPaxIdStr);
    return buildMealsPayload(logicalFlightId, resPaxId, selectedMeal);
  });
};
